import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Toast region**: `aria-live="polite"` on the dock announces new nodes when SR users wait on the page.
 * `pointer-events: none` on dock + `auto` on each toast avoids blocking clicks under the stack; `column-reverse` grows upward visually.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'toast-stack',
  category: 'feedback',
  title: 'Toast stack',
  description: 'Push toasts; auto-remove; stack upward.',
  tags: [T.toast, T.animation],
  html: `<h2>Toasts</h2>

<p><button type="button" id="b1">Notify</button> <button type="button" id="b2">Another</button></p>

<!-- aria-live polite: screen readers hear appended text without stealing focus -->
<div id="dock" class="dock" aria-live="polite"></div>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 1rem;
}

button {
  font: inherit;
  padding: 0.45rem 0.85rem;
  border-radius: 6px;
  border: 1px solid var(--nogrok-preview-primary);
  background: color-mix(in srgb, var(--nogrok-preview-primary) 9%, transparent);
  color: var(--nogrok-preview-on-surface);
  cursor: pointer;
  margin-right: 0.5rem;
}

.dock {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  z-index: 9;
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  background: var(--nogrok-preview-surface-elevated);
  border: 1px solid var(--nogrok-preview-outline);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  animation: inT 0.25s ease;
}

@keyframes inT {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}`,
  js: `let n = 0
const dock = document.getElementById('dock')

function push(t) {
  const el = document.createElement('div')
  el.className = 'toast'
  el.textContent = t
  dock?.appendChild(el)
  // Self-remove: no need to track ids for a toy demo
  window.setTimeout(() => {
    el.remove()
  }, 3200)
}

document.getElementById('b1')?.addEventListener('click', () => push('Saved (demo) ' + ++n))
document.getElementById('b2')?.addEventListener('click', () => push('Queued task'))`,
}

export default preset
